"use server"

import { headers } from "next/headers"
import * as v from "valibot"

import { PUBLIC_MATERIALS } from "@/data/materials"
import { parseBrickEstimate } from "@/lib/brick-estimate"
import {
  type ContactErrors,
  type ContactResult,
  validateContact,
} from "@/lib/contact"
import { resolveContactContext } from "@/lib/contact-context"
import {
  deliverContact,
  deliverContactReceipt,
  deliverMaterialCopy,
  verifyContactChallenge,
} from "@/lib/contact-delivery"
import { deliverContactDiscord } from "@/lib/contact-discord"
import {
  normalizeContactReferenceImage,
  validateContactReferenceImage,
} from "@/lib/contact-reference-image"

import { env } from "../../env"

export async function submitContact(
  formData: FormData,
): Promise<ContactResult> {
  const invalid = {
    status: "error",
    errorType: "validation",
    message: "入力内容を確認してください。",
  } as const
  if (formData.get("website")) return invalid
  const result = validateContact(formData)
  if (!result.success) {
    const nested = v.flatten(result.issues).nested
    const errors: ContactErrors = Object.fromEntries(
      Object.entries(nested ?? {}).map(([key, messages]) => [
        key,
        messages?.[0],
      ]),
    )
    return { ...invalid, errors }
  }
  const materials = result.output.purpose === "materials"
  const downloads = materials
    ? PUBLIC_MATERIALS.filter(({ id }) =>
        result.output.services.includes(id),
      ).map(({ id, name, url }) => ({ id, name, url }))
    : []
  if (materials && downloads.length !== 1)
    return { ...invalid, errors: { genre: "ご希望の資料を選んでください。" } }
  const rawEstimate = formData.get("brickEstimate")
  if (
    rawEstimate !== null &&
    (materials ||
      typeof rawEstimate !== "string" ||
      !parseBrickEstimate(rawEstimate) ||
      !result.output.services.includes("yoshinani-brick"))
  )
    return invalid

  const imageSelection = validateContactReferenceImage(formData, result.output)
  if (!imageSelection.success)
    return { ...invalid, errors: { referenceImage: imageSelection.message } }

  const { CONTACT_FROM_EMAIL: from, TURNSTILE_SECRET_KEY: turnstileSecret } =
    env
  const to = env.CONTACT_TO_EMAILS ?? env.CONTACT_TO_EMAIL
  if (
    !from ||
    !to?.length ||
    !turnstileSecret ||
    !env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  ) {
    return {
      status: "error",
      errorType: "unavailable",
      message:
        "現在、フォームの受付を準備しています。時間をおいて再度お試しください。",
    }
  }
  const stringField = (key: string) => {
    const value = formData.get(key)
    return typeof value === "string" ? value : undefined
  }
  const context = resolveContactContext({
    service: result.output.services[0] ?? "other",
    from: stringField("from"),
    topic: result.output.topic,
    example: stringField("example"),
    photo: stringField("photo"),
    inquiry: stringField("inquiry"),
    brickEstimate: stringField("brickEstimate"),
  })
  const input = { ...result.output, context: context.text }
  const token = formData.get("cf-turnstile-response")
  const origin = (await headers()).get("origin")
  try {
    if (
      typeof token !== "string" ||
      !origin ||
      !(await verifyContactChallenge(
        token,
        turnstileSecret,
        new URL(origin).hostname,
      ))
    ) {
      return {
        status: "error",
        errorType: "challenge",
        message:
          "送信の確認ができませんでした。確認欄を再読み込みして、もう一度お試しください。",
      }
    }
    const imageResult = imageSelection.file
      ? await normalizeContactReferenceImage(imageSelection.file)
      : null
    if (imageResult && !imageResult.success)
      return { ...invalid, errors: { referenceImage: imageResult.message } }
    const delivered = await deliverContact(
      input,
      {
        from,
        to,
        turnstileSecret,
        internalTest: formData.get("internal_test") === "yes",
      },
      imageResult?.success ? imageResult.attachment : undefined,
    )
    if (!delivered) {
      return {
        status: "error",
        errorType: "delivery",
        message:
          "送信できませんでした。入力内容は保持されています。時間をおいて再度お試しください。",
      }
    }
    const config = {
      from,
      to,
      turnstileSecret,
      internalTest: formData.get("internal_test") === "yes",
    }
    // Await every follow-up delivery; one channel's failure does not cancel another.
    const [receiptSent, discordSent] = await Promise.all([
      materials
        ? deliverMaterialCopy(input, downloads, config)
        : deliverContactReceipt(input, config),
      deliverContactDiscord(
        input,
        env.DISCORD_CONTACT_WEBHOOK_URL,
        config.internalTest,
      ),
    ])
    if (!receiptSent) console.warn("contact_delivery_failed: receipt")
    if (!discordSent) console.warn("contact_delivery_failed: discord")
    return {
      status: "success",
      receiptEmail: receiptSent ? "sent" : "failed",
      downloads,
      ...(materials
        ? { emailCopy: receiptSent ? ("sent" as const) : ("failed" as const) }
        : {}),
    }
  } catch {
    // 入力内容・APIキー・プロバイダーのレスポンスはログに残さない。
    return {
      status: "error",
      errorType: "network",
      message:
        "通信を確認できませんでした。入力内容は保持されています。もう一度送信してください。",
    }
  }
}
